import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation } from "react-router-dom";

import {
    getCandidateBgcStatus,
    getEditRequestStatus,
    getApprovedEditRequests,
} from "../../services/candidate/api";

// bgcStatus a candidate has until the declaration is submitted
const NOT_ONBOARDED = "PENDING_SUBMISSION";

const EDIT_ORDER = [
    "PERSONAL_DETAILS",
    "ADDRESS_DETAILS",
    "EDUCATION_DETAILS",
    "EMPLOYMENT_DETAILS",
];

const ROUTE_BY_TYPE = {
    PERSONAL_DETAILS: "/candidate/personal-info",
    ADDRESS_DETAILS: "/candidate/address-info",
    EDUCATION_DETAILS: "/candidate/education-info",
    EMPLOYMENT_DETAILS: "/candidate/employment-info",
};

const byNewest = (a, b) => {
    const dA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dB !== dA ? dB - dA : Number(b.id || 0) - Number(a.id || 0);
};

const CandidateStatusContext = createContext(null);

export const useCandidateStatus = () => {
    const ctx = useContext(CandidateStatusContext);
    if (!ctx) {
        throw new Error(
            "useCandidateStatus must be used inside <CandidateStatusProvider>"
        );
    }
    return ctx;
};

export const CandidateStatusProvider = ({ children }) => {
    const location = useLocation();
    const seq = useRef(0);

    const [state, setState] = useState({
        loading: true,
        error: false,
        onboardingCompleted: null, // null = not known yet
        bgcStatus: null,
        requests: [],
        approved: [],
    });

    const refresh = useCallback(async ({ silent = false } = {}) => {
        const id = ++seq.current;

        if (!silent) {
            setState((s) => ({ ...s, loading: true, error: false }));
        }

        try {
            const bgcStatus = await getCandidateBgcStatus();

            // fail closed: never guess "not onboarded"
            if (!bgcStatus) {
                throw new Error("BGC status missing in getBasicInfo response");
            }

            const onboardingCompleted = bgcStatus !== NOT_ONBOARDED;

            let approved = [];
            let requests = [];

            if (onboardingCompleted) {
                [approved, requests] = await Promise.all([
                    getApprovedEditRequests(),
                    getEditRequestStatus(),
                ]);
            }

            // a newer refresh has started, ignore this result
            if (id !== seq.current) return;

            setState({
                loading: false,
                error: false,
                onboardingCompleted,
                bgcStatus,
                requests,
                approved,
            });
        } catch (e) {
            if (id !== seq.current) return;

            console.error("Failed to load candidate status:", e);

            // a silent (background) refresh keeps the last known good state
            setState((s) =>
                silent ? s : { ...s, loading: false, error: true }
            );
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);


    useEffect(() => {
        if (state.onboardingCompleted) {
            refresh({ silent: true });
        }

    }, [location.pathname]);

    const value = useMemo(() => {
        const latestRequest = (type) =>
            state.requests
                .filter((r) => r.requestType === type)
                .sort(byNewest)[0] ?? null;

        return {
            ...state,
            refresh,
            latestRequest,

            hasActiveEditCycle: state.requests.some(
                (r) => r.status === "PENDING" || r.status === "APPROVED"
            ),

            isApproved: (type) => {
                const latest = latestRequest(type);
                if (latest?.status !== "APPROVED") return false;

                return state.approved.some(
                    (a) =>
                        a.requestType === type &&
                        (!latest.id ||
                            !a.id ||
                            Number(a.id) === Number(latest.id))
                );
            },
            nextEditRoute: (current) => {
                const next = EDIT_ORDER.slice(
                    EDIT_ORDER.indexOf(current) + 1
                ).find((t) => latestRequest(t)?.status === "APPROVED");

                return next ? ROUTE_BY_TYPE[next] : "/candidate/declaration";
            },
        };
    }, [state, refresh]);

    return (
        <CandidateStatusContext.Provider value={value}>
            {children}
        </CandidateStatusContext.Provider>
    );
};